/**
 * Pure helpers for patch-campaign stage state (contract §1).
 *
 * Everything here is a UI convenience derived from server data. The portal API is
 * the only authority: it re-checks every transition, approval rule and
 * precondition, and its 403/409 messages are always surfaced as-is.
 */
import {
  PATCH_ENVIRONMENTS,
  type ComplianceState,
  type MaintenanceWindow,
  type PatchCampaign,
  type PatchEnvironment,
  type PatchMe,
  type PatchPlan,
  type PatchStage,
  type StageStatus,
  type VmAssessment,
  type VmRisk,
} from '@/types/patching'

export const PATCH_POLL_INTERVAL_MS = 5000

export const STAGE_STATUS_LABELS: Record<StageStatus, string> = {
  not_started: 'Not started',
  plan_dispatched: 'Plan dispatched',
  plan_running: 'Planning',
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  execute_dispatched: 'Execute dispatched',
  executing: 'Executing',
  validating: 'Validating',
  accepted: 'Accepted',
  failed: 'Failed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

export function stageStatusLabel(status: string): string {
  return STAGE_STATUS_LABELS[status as StageStatus] ?? status
}

/** Statuses in which the runner is (or is about to be) working — the only ones we poll for. */
const RUNNING_STATUSES: readonly StageStatus[] = [
  'plan_dispatched',
  'plan_running',
  'execute_dispatched',
  'executing',
  'validating',
]

/** No outgoing transition in contract §1 except cancel-from-non-terminal. */
const TERMINAL_STATUSES: readonly StageStatus[] = ['accepted', 'rejected', 'cancelled']

export function isRunningStatus(status: string): boolean {
  return (RUNNING_STATUSES as readonly string[]).includes(status)
}

export function isTerminalStatus(status: string): boolean {
  return (TERMINAL_STATUSES as readonly string[]).includes(status)
}

export function hasRunningStage(campaign: Pick<PatchCampaign, 'stages'> | null | undefined): boolean {
  return !!campaign?.stages?.some((s) => isRunningStatus(s.status))
}

// ─── Stepper display state ───────────────────────────────────────────────────

export type StepState = 'done' | 'active' | 'failed' | 'pending' | 'rejected' | 'cancelled'

/** Map one of the 12 contract §1 statuses onto the six stepper display states. */
export function stepStateFor(status: string): StepState {
  switch (status) {
    case 'accepted':
      return 'done'
    case 'plan_dispatched':
    case 'plan_running':
    case 'awaiting_approval':
    case 'approved':
    case 'execute_dispatched':
    case 'executing':
    case 'validating':
      return 'active'
    case 'failed':
      return 'failed'
    case 'rejected':
      return 'rejected'
    case 'cancelled':
      return 'cancelled'
    case 'not_started':
    default:
      // Unknown values are never shown as done/active.
      return 'pending'
  }
}

// ─── Campaign-level derivations ──────────────────────────────────────────────

export function findStage(
  campaign: Pick<PatchCampaign, 'stages'> | null | undefined,
  env: PatchEnvironment,
): PatchStage | undefined {
  return campaign?.stages?.find((s) => s.environment === env)
}

/**
 * A stage belongs to the campaign when it carries targets. A `not_started` stage
 * whose targets are explicitly null/empty was never requested (e.g. a DEV-only campaign).
 * A started stage is always included; absent `targets` means "server did not say".
 */
export function isStageIncluded(stage: PatchStage | undefined): stage is PatchStage {
  if (!stage) return false
  if (stage.status !== 'not_started') return true
  if (stage.targets === undefined) return true
  return !!stage.targets && stage.targets.vm_names.length > 0
}

export type CampaignBucket = 'active' | 'awaiting' | 'completed' | 'failed'

/**
 * Every campaign lands in exactly one bucket (so tab counts sum to the total).
 * `failed` also holds rejected and cancelled campaigns so nothing silently disappears.
 */
export function campaignBucket(campaign: PatchCampaign): CampaignBucket {
  const included = PATCH_ENVIRONMENTS.map((e) => findStage(campaign, e)).filter(isStageIncluded)
  if (included.some((s) => s.status === 'failed' || s.status === 'rejected' || s.status === 'cancelled')) {
    return 'failed'
  }
  if (included.some((s) => s.status === 'awaiting_approval')) return 'awaiting'
  if (included.length > 0 && included.every((s) => s.status === 'accepted')) return 'completed'
  return 'active'
}

/** Short "where is it now" summary for the list, e.g. "UAT · Awaiting approval". */
export function campaignHeadline(campaign: PatchCampaign): string {
  const included = PATCH_ENVIRONMENTS.map((e) => findStage(campaign, e)).filter(isStageIncluded)
  if (included.length === 0) return 'No stages'
  const attention =
    included.find((s) => s.status === 'failed' || s.status === 'rejected' || s.status === 'cancelled') ??
    included.find((s) => s.status !== 'accepted')
  if (!attention) return 'Completed'
  return `${attention.environment.toUpperCase()} · ${stageStatusLabel(attention.status)}`
}

// ─── Plan & risk helpers ─────────────────────────────────────────────────────

export function isFlaggedPlan(plan: PatchPlan | null | undefined): boolean {
  return !!plan && (plan.destructive || plan.scope_expanding || plan.requires_additional_review === true)
}

export function blockedRisks(stage: PatchStage): VmRisk[] {
  return (stage.risks ?? []).filter((r) => r.blocked)
}

/**
 * Contract §11.7: a VM that could not be assessed has `assessed: false` and null counts.
 * If an older payload omits `assessed`, a VM is only treated as assessed when it has counts.
 */
export function isAssessed(vm: Pick<VmAssessment, 'assessed' | 'missing'>): boolean {
  if (vm.assessed === false) return false
  return vm.missing !== null && vm.missing !== undefined
}

/**
 * Risk rule 2 (contract §10/§11.4): reboot on a critical single-instance workload needs an outage
 * acknowledgement that only an approver can give. The portal signals it with
 * `controls.requires_outage_ack` and/or a reason that names the outage.
 */
export function isOutageWaivable(risk: VmRisk): boolean {
  if (risk.controls && risk.controls['requires_outage_ack'] === true) return true
  return (risk.reasons ?? []).some((reason) => /outage/i.test(reason))
}

/** The outage-acknowledgement checkbox is shown only when some VM's risk asks for it. */
export function needsOutageAck(stage: PatchStage): boolean {
  return (stage.risks ?? []).some(isOutageWaivable)
}

/** Blocked VMs that no approver acknowledgement can unblock (e.g. no recovery point for prod). */
export function hardBlockedRisks(stage: PatchStage): VmRisk[] {
  return blockedRisks(stage).filter((r) => !isOutageWaivable(r))
}

/** The approver-set window: on the stage, else on the latest approval that carries one. */
export function approvedWindow(stage: PatchStage): MaintenanceWindow | null {
  if (stage.maintenance_window) return stage.maintenance_window
  const approvals = stage.approvals ?? []
  for (let i = approvals.length - 1; i >= 0; i -= 1) {
    const window = approvals[i].maintenance_window
    if (approvals[i].decision === 'approved' && window) return window
  }
  return null
}

/** Does the stage have anything to explain in a banner (failure, silence, cancel outcome, window…)? */
export function hasStageNotices(stage: PatchStage): boolean {
  return (
    stage.status === 'failed' ||
    !!stage.failure ||
    stage.acceptance?.passed === false ||
    stage.status === 'rejected' ||
    !!stage.stale ||
    !!stage.dispatch_unconfirmed ||
    (!!stage.run_cancel && stage.run_cancel.state !== 'none') ||
    !!approvedWindow(stage)
  )
}

/** Cancel could not confirm the GitHub run stopped (contract §11.3). */
export function runMayStillBeActive(stage: PatchStage): boolean {
  const state = stage.run_cancel?.state
  return state === 'failed' || state === 'unresolved'
}

// ─── Action gating (convenience only — the server decides) ───────────────────

export interface ActionGate {
  visible: boolean
  /** When visible but not usable: why (shown to the user). */
  disabledReason: string | null
}

export interface StageActionGates {
  plan: ActionGate
  approve: ActionGate
  reject: ActionGate
  execute: ActionGate
  schedule: ActionGate
  cancel: ActionGate
}

export function sameUser(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/** Requester-or-admin gate for plan/execute. If the requester is not an e-mail we cannot tell, so we defer to the server. */
export function canDispatch(me: PatchMe | undefined, campaign: PatchCampaign): boolean {
  if (!me) return false
  if (me.is_patch_admin) return true
  if (!me.can_request) return false
  const requester = campaign.requested_by
  if (typeof requester === 'string' && requester.includes('@')) return sameUser(me.email, requester)
  return true
}

function promotionBlocker(campaign: PatchCampaign, env: PatchEnvironment): string | null {
  const predecessors: PatchEnvironment[] = env === 'uat' ? ['dev'] : env === 'prod' ? ['dev', 'uat'] : []
  for (const p of predecessors) {
    const stage = findStage(campaign, p)
    if (isStageIncluded(stage) && stage.status !== 'accepted') {
      return `${p.toUpperCase()} must be accepted first.`
    }
  }
  return null
}

const HIDDEN: ActionGate = { visible: false, disabledReason: null }
const SHOWN: ActionGate = { visible: true, disabledReason: null }

export function getStageActionGates(
  me: PatchMe | undefined,
  campaign: PatchCampaign,
  stage: PatchStage,
): StageActionGates {
  const env = stage.environment
  const dispatcher = canDispatch(me, campaign)
  const isApprover = !!me && me.approver_for.includes(env)
  const status = stage.status
  const blockedMessage = (risks: VmRisk[]) =>
    risks.length > 0
      ? `Blocked by risk policy on ${risks.map((r) => r.vm_name).join(', ')} — approval and execution are not possible until resolved.`
      : null
  // An outage acknowledgement (given with the approval) can lift rule-2 blocks; nothing else can.
  const approveBlock = blockedMessage(hardBlockedRisks(stage))
  const outageAcknowledged = (stage.approvals ?? []).some((a) => a.decision === 'approved' && a.acknowledged_outage === true)
  const executeBlock = blockedMessage(
    blockedRisks(stage).filter((r) => !(outageAcknowledged && isOutageWaivable(r))),
  )
  const unconfirmed = stage.dispatch_unconfirmed
    ? 'The last dispatch was not confirmed, so it cannot be dispatched again. Cancel it if you are unsure.'
    : null

  // A cancelled stage can be re-planned (contract §11.3: cancelled → plan_dispatched).
  let plan: ActionGate = HIDDEN
  if (dispatcher && (status === 'not_started' || status === 'failed' || status === 'cancelled')) {
    plan = { visible: true, disabledReason: promotionBlocker(campaign, env) ?? unconfirmed }
  }

  // Contract §11.6: the portal refuses to approve a destructive plan at all.
  const destructiveBlock = stage.plan?.destructive
    ? 'This plan is destructive (it deletes or replaces resources). It cannot be approved in this pipeline — resolve it outside the pipeline, then plan again.'
    : null
  const approve: ActionGate =
    isApprover && status === 'awaiting_approval'
      ? { visible: true, disabledReason: destructiveBlock ?? approveBlock }
      : HIDDEN
  const reject: ActionGate = isApprover && status === 'awaiting_approval' ? SHOWN : HIDDEN

  let execute: ActionGate = HIDDEN
  let schedule: ActionGate = HIDDEN
  if (dispatcher && status === 'approved') {
    const reason = executeBlock ?? promotionBlocker(campaign, env) ?? unconfirmed
    execute = { visible: true, disabledReason: reason }
    schedule = { visible: true, disabledReason: reason }
  }

  const cancellable = !isTerminalStatus(status)
  const cancel: ActionGate =
    cancellable && (dispatcher || isApprover || !!me?.is_patch_admin) ? SHOWN : HIDDEN

  return { plan, approve, reject, execute, schedule, cancel }
}

// ─── Compliance wording (contract §1/§6/§8) ─────────────────────────────────

export const COMPLIANCE_COPY: Record<ComplianceState, { label: string; explain: string }> = {
  compliant: {
    label: 'Compliant',
    explain:
      'Verified: the install reached a terminal success state, any required reboot completed, all mandatory validations passed and a fresh post-assessment shows no remaining in-scope updates.',
  },
  noncompliant: {
    label: 'Non-compliant',
    explain: 'Verification ran and found remaining in-scope updates or a failed mandatory check.',
  },
  unverified: {
    label: 'Unverified',
    explain:
      'There is no verification evidence for this VM (for example no fresh post-assessment). It is NOT counted as compliant.',
  },
  unsupported: {
    label: 'Unsupported',
    explain:
      "Update Manager does not support this VM's OS or image, so its patch state cannot be verified. It is NOT counted as compliant.",
  },
}

export function isComplianceState(value: unknown): value is ComplianceState {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(COMPLIANCE_COPY, value)
}

/** Missing or unrecognised values are `unverified` — never `compliant`. */
export function effectiveCompliance(value: unknown): ComplianceState {
  return isComplianceState(value) ? value : 'unverified'
}

/** Only http(s) links from the server are rendered as links (never `javascript:` and friends). */
export function safeExternalUrl(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null
  } catch {
    return null
  }
}

/** Local executor marker. Opening it leaves the app for a host that is not listening. */
export function isLocalRunUrl(url: string | null | undefined): boolean {
  if (!url) return false
  try {
    const parsed = new URL(url)
    const local = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1'
    return local && (parsed.pathname.includes('/patching/local-runs') || parsed.hash.includes('local-run'))
  } catch {
    return false
  }
}

// ─── Dry-run pre-flight display (shape tolerated, never assumed) ─────────────

export interface PreflightLine {
  label: string
  /** null = the item does not say. */
  passed: boolean | null
  detail: string | null
  /** Nested validation checks (a VM's pre-flight carries its `validation[]`). */
  checks: PreflightLine[]
  /** For nested checks: mandatory vs optional; null when not stated. */
  mandatory: boolean | null
}

function textOf(value: unknown): string | null {
  if (typeof value === 'string') return value || null
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return null
}

/** Render one pre-flight entry from whichever of the plausible shapes the portal sends. */
export function describePreflightItem(item: unknown): PreflightLine {
  if (typeof item === 'string') return { label: item, passed: null, detail: null, checks: [], mandatory: null }
  if (typeof item !== 'object' || item === null) {
    return { label: JSON.stringify(item), passed: null, detail: null, checks: [], mandatory: null }
  }
  const record = item as Record<string, unknown>
  const label =
    ['check', 'gate', 'name', 'vm_name', 'id', 'stage'].map((k) => textOf(record[k])).find((v) => v !== null) ??
    JSON.stringify(item)
  let passed: boolean | null = null
  if (typeof record['passed'] === 'boolean') passed = record['passed']
  else if (typeof record['ok'] === 'boolean') passed = record['ok']
  else if (typeof record['status'] === 'string') {
    const status = record['status'].toLowerCase()
    if (['passed', 'pass', 'ok', 'succeeded', 'success'].includes(status)) passed = true
    else if (['failed', 'fail', 'error', 'blocked'].includes(status)) passed = false
  }
  const detail =
    ['detail', 'message', 'reason', 'error', 'skipped_reason'].map((k) => textOf(record[k])).find((v) => v !== null) ?? null
  const checks = Array.isArray(record['validation']) ? record['validation'].map(describePreflightItem) : []
  const mandatory = typeof record['mandatory'] === 'boolean' ? record['mandatory'] : null
  return { label, passed, detail, checks, mandatory }
}

/** Contract §11.1 `compliance_notes` may be a string or a list of strings. */
export function complianceNotes(value: string[] | string | null | undefined): string[] {
  if (!value) return []
  return (Array.isArray(value) ? value : [value]).map((v) => textOf(v) ?? JSON.stringify(v)).filter(Boolean)
}
